import { forwardRef } from "react";
import { FileX } from "@semo/icons";
import type { ChatContractCancelledCardProps } from "../types";
import { cn, ReceiptButton } from "@semo/shared-ui";
import { MessageCardWrapper } from "../layout/MessageCardWrapper";

/**
 * ChatContractCancelledCard - 계약 취소 알림 카드
 *
 * 계약이 취소되었음을 알리는 카드입니다.
 * 취소 영수증 확인 기능을 제공합니다.
 */
export const ContractCancelledCard = forwardRef<
  HTMLDivElement,
  ChatContractCancelledCardProps
>(({ message, sender, tid }, ref) => {
  const { createdAt } = message;

  const time = new Date(createdAt).toLocaleString("ko-KR", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <MessageCardWrapper
      ref={ref}
      data-slot="chat-contract-cancelled-card"
      className={cn(
        "bg-white ",
        "border rounded-xl p-3 sm:p-4 shadow-sm relative overflow-hidden",
        sender === "me" ? "border-red-500/30 " : "border-red-200 ",
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 pb-2 sm:pb-3 border-b border-slate-100 ">
        <div
          className={cn(
            "w-8 h-8 sm:w-10 sm:h-10 rounded-lg",
            "bg-red-50 ",
            "flex items-center justify-center",
            "text-red-600  shrink-0",
          )}
        >
          <FileX className="w-4 h-4 sm:w-5 sm:h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm sm:text-sm font-semibold text-slate-900  truncate">
            계약 취소 알림
          </div>
          <div className="text-2xs sm:text-xs text-slate-500 ">
            {time}
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div
        className={cn(
          "bg-red-50/50 ",
          "rounded-lg p-3 mb-1",
          "text-xs sm:text-sm text-slate-600  leading-relaxed",
        )}
      >
        계약이 취소되었습니다.
      </div>

      {/* Action Button */}
      {tid && (
        <div className="flex gap-2 mt-3">
          <ReceiptButton
            tid={tid}
            variant="outline"
            className="flex-1 py-2 h-auto text-xs sm:text-sm font-semibold rounded-lg text-slate-600 hover:text-slate-900 border-slate-200"
          >
            취소 영수증
          </ReceiptButton>
        </div>
      )}
    </MessageCardWrapper>
  );
});

ContractCancelledCard.displayName = "ChatContractCancelledCard";
